import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'
import { defaultSubpaths } from './default-subpaths'

/** Roots of the derivative of a cubic bezier dimension in (0,1), where curve extrema occur. */
export function cubicExtremaT(p0: number, p1: number, p2: number, p3: number): number[] {
  const a = -p0 + 3 * p1 - 3 * p2 + p3
  const b = 2 * (p0 - 2 * p1 + p2)
  const c = p1 - p0
  const ts: number[] = []

  if (Math.abs(a) < 1e-10) {
    if (Math.abs(b) > 1e-10) {
      const t = -c / b

      if (t > 0 && t < 1) ts.push(t)
    }
  } else {
    const disc = b * b - 4 * a * c

    if (disc >= 0) {
      const sq = Math.sqrt(disc)

      for (const t of [(-b + sq) / (2 * a), (-b - sq) / (2 * a)]) {
        if (t > 0 && t < 1) ts.push(t)
      }
    }
  }

  return ts
}

export function cubicAt(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const u = 1 - t

  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3
}

/**
 * Tight bounding box of a pen path including bezier curve extrema.
 * Handles may extend beyond the returned bounds; this captures where the curve actually goes.
 * Coordinates are in the same local space as the points.
 */
export function pathTightBounds(
  points: PenAnchorPoint[],
  isClosed: boolean,
  subpaths?: PenSubpathRange[]
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (points.length === 0) return null

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity

  for (const pt of points) {
    if (pt.x < minX) minX = pt.x
    if (pt.y < minY) minY = pt.y
    if (pt.x > maxX) maxX = pt.x
    if (pt.y > maxY) maxY = pt.y
  }

  const ranges = defaultSubpaths(points, isClosed, subpaths)

  for (const range of ranges) {
    const n = range.end - range.start

    if (n < 1) continue

    const segCount = range.isClosed ? n : n - 1

    for (let k = 0; k < segCount; k++) {
      const curr = points[range.start + k]
      const next = points[range.start + ((k + 1) % n)]

      if (!curr.handleOut && !next.handleIn) continue

      const cp1x = curr.handleOut?.x ?? curr.x
      const cp1y = curr.handleOut?.y ?? curr.y
      const cp2x = next.handleIn?.x ?? next.x
      const cp2y = next.handleIn?.y ?? next.y

      for (const t of cubicExtremaT(curr.x, cp1x, cp2x, next.x)) {
        const x = cubicAt(curr.x, cp1x, cp2x, next.x, t)

        if (x < minX) minX = x
        if (x > maxX) maxX = x
      }

      for (const t of cubicExtremaT(curr.y, cp1y, cp2y, next.y)) {
        const y = cubicAt(curr.y, cp1y, cp2y, next.y, t)

        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }

  return { minX, minY, maxX, maxY }
}
